/**
 * Layout constants for consistent spacing and dimensions
 */

// Sidebar dimensions
export const SIDEBAR_WIDTH = 255 // px

// Common spacing values
export const LAYOUT_SPACING = {
  // Padding values
  PADDING_XS: 13, // 0.8125rem ≈ 0.8rem
  PADDING_SM: 16, // 1rem
  PADDING_MD: 24, // 1.5rem
  PADDING_LG: 32, // 2rem

  // Margin values
  MARGIN_SM: 8, // 0.5rem
  MARGIN_MD: 16, // 1rem
  MARGIN_LG: 24, // 1.5rem

  // Gap values
  GAP_XS: 4, // 0.25rem
  GAP_SM: 8, // 0.5rem
  GAP_MD: 16, // 1rem
  GAP_LG: 24, // 1.5rem

  // Sidebar specific spacing
  SIDEBAR_CONTAINER_PADDING: 16, // Sidebar container padding
  SIDEBAR_LIST_GAP: 12, // Gap between sidebar items
  SIDEBAR_ITEM_PADDING_VERTICAL: 16, // Sidebar item vertical padding
  SIDEBAR_ITEM_PADDING_HORIZONTAL: 8, // Sidebar item horizontal padding
  SIDEBAR_ITEM_GAP: 8, // Gap between icon and text

  // Flow component spacing
  FLOW_HEADER_MARGIN: 32, // 2rem
  FLOW_STEP_MARGIN: 12, // 0.75rem
  FLOW_TITLE_MARGIN: 8, // 0.5rem

  // Responsive padding for page containers
  RESPONSIVE_PADDING: {
    // Container padding for page content
    CONTAINER: {
      xs: 13, // PADDING_XS
      sm: 16, // PADDING_SM
      md: 24, // PADDING_MD
      lg: 32 // PADDING_LG
    },
    // Section padding (slightly smaller)
    SECTION: {
      xs: 13, // PADDING_XS
      sm: 16, // PADDING_SM
      md: 24, // PADDING_MD
      lg: 24 // PADDING_MD (same as md)
    }
  },

  // Responsive gap for grid layouts
  RESPONSIVE_GAP: {
    // Gap between grid items
    GRID: {
      xs: 8, // GAP_SM
      sm: 16, // GAP_MD
      md: 16, // GAP_MD
      lg: 24 // GAP_LG
    }
  }
} as const

// Component dimensions
export const COMPONENT_DIMENSIONS = {
  // Avatar sizes
  AVATAR_SM: 32, // 8 * 4px
  AVATAR_MD: 40, // 10 * 4px
  AVATAR_LG: 48, // 12 * 4px

  // Icon sizes
  ICON_SM: 16, // 4 * 4px
  ICON_MD: 20, // 5 * 4px
  ICON_LG: 24, // 6 * 4px

  // Step Progress sizes
  STEP_PROGRESS_SM: 32, // 8 * 4px
  STEP_PROGRESS_MD: 40, // 10 * 4px
  STEP_PROGRESS_LG: 48, // 12 * 4px

  // Flow step sizes
  FLOW_STEP_SIZE: 48, // 12 * 4px
  FLOW_STEP_HALF: 24, // 6 * 4px (half of step size)

  // Border widths
  BORDER_THIN: 1,
  BORDER_MEDIUM: 2,
  BORDER_THICK: 4,

  // Border radius
  BORDER_RADIUS_SM: 6, // Small radius (sidebar items)
  BORDER_RADIUS_MD: 8, // Medium radius (active sidebar items)
  BORDER_RADIUS_LG: 10, // Large radius
  BORDER_RADIUS_XL: 16 // Extra large radius
} as const

// Z-index values
export const Z_INDEX = {
  DROPDOWN: 50,
  MODAL: 100,
  TOOLTIP: 200,
  NOTIFICATION: 300,
  // Flow component z-indexes
  FLOW_BACKGROUND_LINE: 0,
  FLOW_COMPLETED_SEGMENT: 5,
  FLOW_STEP_BUTTON: 10
} as const

// Breakpoints (matching Tailwind defaults)
export const BREAKPOINTS = {
  SM: 640, // 40rem
  MD: 768, // 48rem
  LG: 1024, // 64rem
  XL: 1280, // 80rem
  '2XL': 1536 // 96rem
} as const

// Step Progress enums
export const STEP_PROGRESS_SIZE = {
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large'
} as const

export const STEP_PROGRESS_STATE = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  COMPLETED: 'completed'
} as const

export type StepProgressSize = (typeof STEP_PROGRESS_SIZE)[keyof typeof STEP_PROGRESS_SIZE]
export type StepProgressState = (typeof STEP_PROGRESS_STATE)[keyof typeof STEP_PROGRESS_STATE]
